import { get, geti18n } from '../../utils';

/*
 * Selector Functions for retrieving items from the state for the best offer.
 */

/**
 * Retrieve the Best Lease offer from the Store
 * @param state - The Redux store
 * @returns {*}
 */
const getBestLeaseOffer = (state) => {
  if (state.bestOffers.offers &&
    state.bestOffers.offers.hasValidData &&
    state.bestOffers.offers.hasValidLease) {
    return state.bestOffers.offers.bestLeaseOffer;
  }
  return false;
};

/**
 * Retrieve the Best Finance Offer from the Store
 * @param state - The Redux store
 * @returns {*}
 */
const getBestFinanceOffer = (state) => {
  if (state.bestOffers.offers &&
    state.bestOffers.offers.hasValidData &&
    state.bestOffers.offers.hasValidFinance) {
    return state.bestOffers.offers.bestFinanceOffer;
  }
  return false;
};

/**
 * Sanitize the Offer before sending it out, adds on the payment type
 * and the React-Key.
 * @param option - The payment option
 * @param paymentType - The type of the payment (OFFER, INCENTIVE)
 * @returns {{reactKey: string, paymentType: *}}
 */
const sanitizeOffer = (option, paymentType) => {
  const key = option.theOfferData ? `-${option.theOfferData.promoID}` : '';
  const optionType = option.theOfferData.isOffer === true ? 'OFFER' : 'INCENTIVE';
  return {
    ...option,
    reactKey: `${paymentType}-${optionType}${key}`,
    paymentType
  }
};

/**
 * Retrieve and Sanitize the Best Finance option
 * @param state - The Redux store
 * @returns {*}
 */
export const getBestFinanceSanitized = (state) => {
  const offer = getBestFinanceOffer(state);
  if (offer) {
    return sanitizeOffer(offer, 'FINANCE');
  }
  return false
};

/**
 * Retrieve and Sanitize the Best Lease option
 * @param state - The Redux store
 * @returns {*}
 */
export const getBestLeaseSanitized = (state) => {
  const offer = getBestLeaseOffer(state);
  if (offer) {
    return sanitizeOffer(offer, 'LEASE');
  }
  return false
};

/**
 * Returns the Acquisition for a provided Lease option
 * @param paymentOption - The Lease option
 * @param term - The selected term
 * @param mileage - The selected mileage
 * @returns {boolean}
 */
export const getAcquisitionFee = (paymentOption, term, mileage) => {
  if (paymentOption.theOfferData.terms[mileage]) {
    return paymentOption.theOfferData.terms[mileage][term].acquisitionFee;
  }
  return false;
};

/* eslint-disable */
/**
 * Return the offer Array's Offer flagged with the Best APR
 * @param offers
 * @returns {*}
 */
export const getBestAPROffer = (offers) => {
  let bestAPR;
  for (const key in offers) {
    if (offers.hasOwnProperty(key)) {
      if (offers[key].isBestAPR) {
        bestAPR = offers[key];
      }
    }
  }
  return bestAPR;
};

/**
 * Return the offer Array's Offer flagged with the Best APR Highest Cashback
 * @param offers
 * @returns {*}
 */
export const getBestAPRHighestCashback = (offers) => {
  let bestAPRHighestCashback;
  for (const key in offers) {
    if (offers.hasOwnProperty(key)) {
      if (offers[key].isBestAprHighestCashback) {
        bestAPRHighestCashback = offers[key];
      }
    }
  }
  return bestAPRHighestCashback;
};

/**
 * Return the offer Array's Offer flagged with the Highest Cashback
 * @param offers
 * @returns {*}
 */
export const getHighestCashback = (offers) => {
  let highestCashback;
  for (const key in offers) {
    if (offers.hasOwnProperty(key)) {
      if (offers[key].isHighestCashback) {
        highestCashback = offers[key];
      }
    }
  }
  return highestCashback;
};

/**
 * Return the offer Array's Offer flagged with the Lowest Monthly Payment
 * @param offers
 * @returns {*}
 */
export const getLowestPayment = (offers) => {
  let lowestPayment;
  for (const key in offers) {
    if (offers.hasOwnProperty(key)) {
      if (offers[key].isLowestPayment) {
        lowestPayment = offers[key];
      }
    }
  }
  return lowestPayment;
};

/*eslint-enable*/

/**
 * Return a Finance offer by the selected Term
 * @param state - The Redux store
 * @returns {*}
 */
export const getFinanceByTerm = (state) => {
  const selectedTerm = get(state, 'bestOffers.userValues.FINANCE.selectedTerm');
  const selectedFinanceDropdown = get(state, 'bestOffers.userValues.FINANCE.selectedOfferType') || '';
  const selectedTermData = get(state, `bestOffers.offers.groupedFinanceOffers.${selectedTerm}`);
  if (selectedTermData) {
    // Need to select Offer to use.
    let offerOption = getBestAPROffer(selectedTermData);
    switch (selectedFinanceDropdown) {
      case 'bestAPRCashback':
        if (getBestAPRHighestCashback(selectedTermData)) {
          offerOption = getBestAPRHighestCashback(selectedTermData);
        }
        break;
      case 'highestCashback':
        if (getHighestCashback(selectedTermData)) {
          offerOption = getHighestCashback(selectedTermData);
        }
        break;
      case 'lowestMonthly':
        if (getLowestPayment(selectedTermData)) {
          offerOption = getLowestPayment(selectedTermData);
        }
        break;
      default:
    }
    return sanitizeOffer(offerOption, 'FINANCE');
  } else { //eslint-disable-line
    return getBestFinanceSanitized(state);
  }
};
/**
 * Return a Lease offer by the selected Term and Mileage
 * @param state - The Redux store
 * @returns {*}
 */
export const getLeaseByTerm = (state) => {
  const selectedTerm = get(state, 'bestOffers.userValues.LEASE.selectedTerm');
  const selectedMileage = get(state, 'bestOffers.userValues.LEASE.selectedMileage');
  const selectedTermData = get(state, `bestOffers.offers.groupedLeaseOffers.${selectedTerm}/${selectedMileage}`);

  if (selectedTerm && selectedMileage && selectedTermData) {
    return sanitizeOffer(selectedTermData, 'LEASE');
  } else { //eslint-disable-line
    return getBestLeaseSanitized(state);
  }
};

/* eslint-disable */
/**
 * Based on what Offer options are available create the options
 * for the dropdown section of the finance offer
 * @param state - The Redux store
 * @param term - The term selected
 * @returns {Array}
 */
export const createFinanceOfferOptions = (state, term) => {
  const selectedTermData = get(state, `bestOffers.offers.groupedFinanceOffers.${term}`);
  let displayOptions = [];
  let menuOptions = [];
  if (selectedTermData) {
    selectedTermData.map(offer => {
      const aprString = `${offer.apr}%` || '';
      const cashBackAPRString = `${aprString}/$${offer.cashBack}`;
      const finalString = offer.cashBack ? cashBackAPRString : aprString;
      const bestAPRLabel = geti18n('offerTypeDropdown.bestAPR');
      const bestAPRCashbackLabel = geti18n('offerTypeDropdown.bestAPRCashback');
      const lowestPaymentLabel = geti18n('offerTypeDropdown.lowestMonthly');
      const highestCashbackLabel = geti18n('offerTypeDropdown.highestCashback');
      if (offer.isBestAPR) {
        displayOptions.push({ text: bestAPRLabel, value: 'bestAPR', key: 1 });
        menuOptions.push({ text: `${bestAPRLabel}: ${finalString}`, value: 'bestAPR', key: 2 });
      }
      if (offer.isBestAprHighestCashback) {
        displayOptions.push({ text: bestAPRCashbackLabel, value: 'bestAPRCashback', key: 3 });
        menuOptions.push({ text: `${bestAPRCashbackLabel}: ${finalString}`, key: 4, value: 'bestAPRCashback' });
      }
      if (offer.isLowestPayment) {
        displayOptions.push({ text: lowestPaymentLabel, value: 'lowestMonthly', key: 5 });
        menuOptions.push({ text: `${lowestPaymentLabel}: ${finalString}`, value: 'lowestMonthly', key: 6 });
      }
      if (offer.isHighestCashback) {
        displayOptions.push({ text: highestCashbackLabel, value: 'highestCashback', key: 7 });
        menuOptions.push({ text: `${highestCashbackLabel}: ${finalString}`, value: 'highestCashback', key: 8 });
      }
    });
  }
  return { displayOptions, menuOptions };
};

/* eslint-enable */



/** WEBPACK FOOTER **
 ** ./src/redux/selectors/bestOffer.js
 **/